// Page: ManageEnterprises
// Purpose: Admin list page showing all enterprises with navigation to create,
// edit, delete (with stats), and manage a specific enterprise.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { PageHeader } from '../../../components/ui/layout/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/layout/card';
import { ConfirmationModal } from '../../../components/ui/modals/confirmation-modal.jsx';
import { Building2, Plus, Edit3, Trash2, Users, ArrowLeft } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import api from '../../../api';

export default function ManageEnterprises() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [enterprises, setEnterprises] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [enterpriseToDelete, setEnterpriseToDelete] = useState(null);
  const [deletingEnterprise, setDeletingEnterprise] = useState(false);
  const [fetchInProgress, setFetchInProgress] = useState(false);

  // Check if user is admin
  const isAdmin = isUserAdmin(user);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        fetchEnterprises();
      }
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  const fetchEnterprises = async () => {
    // Prevent multiple simultaneous calls
    if (fetchInProgress) {
      console.log('Fetch already in progress, skipping...');
      return;
    }
    
    setFetchInProgress(true);
    try {
      const response = await api.get('/admin/enterprises');
      
      // The API core returns the parsed JSON directly
      // Server returns { enterprises: [...] }, so response.enterprises should have the array
      const enterprises = response?.enterprises || [];
      
      setEnterprises(enterprises);
    } catch (err) {
      console.error('Error fetching enterprises:', err);
      addToast('Failed to load enterprises', 'error');
    } finally {
      setIsLoading(false);
      setFetchInProgress(false);
    }
  };

  const handleDeleteEnterprise = async () => {
    if (!enterpriseToDelete) return;

    setDeletingEnterprise(true);
    try {
      const response = await api.del(`/admin/enterprises/${enterpriseToDelete.id}`);
      
      // Handle the enhanced deletion response with statistics
      const deletionStats = response.stats;
      
      if (deletionStats) {
        const {
          usersDeleted,
          namespacesDeleted,
          campaignsDeleted,
          videosDeleted,
          customTemplatesDeleted,
          errors
        } = deletionStats;
        
        let successMessage = `Enterprise "${enterpriseToDelete.enterpriseName}" deleted successfully`;
        
        if (usersDeleted > 0) {
          successMessage += ` along with ${usersDeleted} user${usersDeleted > 1 ? 's' : ''}`;
          
          const deletedItems = [];
          if (namespacesDeleted > 0) deletedItems.push(`${namespacesDeleted} namespace${namespacesDeleted > 1 ? 's' : ''}`);
          if (campaignsDeleted > 0) deletedItems.push(`${campaignsDeleted} campaign${campaignsDeleted > 1 ? 's' : ''}`);
          if (videosDeleted > 0) deletedItems.push(`${videosDeleted} video${videosDeleted > 1 ? 's' : ''}`);
          if (customTemplatesDeleted > 0) deletedItems.push(`${customTemplatesDeleted} custom template${customTemplatesDeleted > 1 ? 's' : ''}`);
          
          if (deletedItems.length > 0) {
            successMessage += ` and their associated data (${deletedItems.join(', ')})`;
          }
        }
        
        addToast(successMessage, 'success');
        
        // Show warnings for any errors that occurred during user deletion
        if (errors && errors.length > 0) {
          errors.forEach(error => {
            addToast(`Warning: Failed to delete user ${error.email}: ${error.error}`, 'warning');
          });
        }
      } else {
        addToast(`Enterprise "${enterpriseToDelete.enterpriseName}" deleted successfully`, 'success');
      }
      
      // Remove from local state
      setEnterprises(prev => prev.filter(e => e.id !== enterpriseToDelete.id));
      
      setShowDeleteModal(false);
      setEnterpriseToDelete(null);
    } catch (err) {
      console.error('Error deleting enterprise:', err);
      const errorMessage = err.response?.data?.error || 'Failed to delete enterprise';
      addToast(errorMessage, 'error');
    } finally {
      setDeletingEnterprise(false);
    }
  };

  const confirmDelete = (enterprise) => {
    setEnterpriseToDelete(enterprise);
    setShowDeleteModal(true);
  };

  const handleEditEnterprise = (enterprise) => {
    navigate(`/app/private/editEnterprise/${enterprise.id}`, {
      state: { enterprise }
    });
  };

  const handleManageEnterprise = (enterprise) => {
    navigate(`/app/private/manageEnterprise/${enterprise.id}`, {
      state: { enterprise }
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading enterprises..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Admin
        </button>
      </div>

      <PageHeader
        title="Manage Enterprises"
        description="View and manage enterprise accounts with custom plan configurations"
      >
        <button
          onClick={() => navigate('/app/private/createEnterprise')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4" />
          Create Enterprise
        </button>
      </PageHeader>

      {/* Enterprises List */}
      <div className="space-y-6 mt-8">
        {enterprises.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No enterprises yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Create your first enterprise account to get started.
              </p>
              <button
                onClick={() => navigate('/app/private/createEnterprise')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mx-auto"
              >
                <Plus className="h-4 w-4" />
                Create Enterprise
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {enterprises.map((enterprise) => (
              <Card key={enterprise.id}>
                <CardContent className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 -m-2 transition-colors"
                    onClick={() => handleManageEnterprise(enterprise)}
                  >
                    {/* Left side - Enterprise info */}
                    <div className="flex items-center gap-4 flex-1">
                      {(enterprise.enterpriseImageUrl || enterprise.enterpriseImage) ? (
                        <img
                          src={enterprise.enterpriseImageUrl || enterprise.enterpriseImage}
                          alt={`${enterprise.enterpriseName} logo`}
                          className="h-10 w-10 object-contain rounded"
                        />
                      ) : (
                        <div className="h-10 w-10 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-4 flex-wrap">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {enterprise.enterpriseName}
                          </h4>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {enterprise.enterpriseDomain}
                          </span>
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                            Premium
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Users className="h-3 w-3" />
                            {enterprise.maxAccounts ? `Max ${enterprise.maxAccounts} users` : 'Unlimited users'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right side - Action buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEnterprise(enterprise);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
                      >
                        <Edit3 className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete(enterprise);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700/50 dark:hover:bg-red-900/30"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setEnterpriseToDelete(null);
        }}
        onConfirm={handleDeleteEnterprise}
        title="Delete Enterprise & All User Data"
        message={
          <div className="space-y-3">
            <p>
              Are you sure you want to delete <strong>{enterpriseToDelete?.enterpriseName}</strong>?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-md p-3 dark:bg-red-900/20 dark:border-red-800">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                ⚠️ This will permanently delete:
              </p>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 ml-4">
                <li>• All users in this enterprise</li>
                <li>• All user namespaces and campaigns</li>
                <li>• All survey videos and responses</li>
                <li>• All custom templates</li>
                <li>• All user activities and data</li>
                <li>• All associated storage files</li>
              </ul>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This action cannot be undone and will permanently remove all data associated with this enterprise.
            </p>
          </div>
        }
        confirmButtonText="Delete Enterprise & All Data"
        isLoading={deletingEnterprise}
      />

      <Footer />
    </div>
  );
} 